import { useMemo, useState } from "react";
import useUserPageParams from "../../../../../hooks/useUserPageParams";
import { MagnifyingGlassIcon, CalendarIcon, PlusIcon } from "@heroicons/react/20/solid";
import {
  Alert,
  Button,
  Center,
  Divider,
  Group,
  LoadingOverlay,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
} from "@mantine/core";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { AssociatedObject, MeetingsResponse } from "../../../../../shared/emails-meetings/types";
import { getContactMeetings } from "../../../../../services/associatedObjectsEndpoint";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import MeetingListItem from "../../../../../shared/emails-meetings/MeetingListItem";
import MeetingDetailsDrawer from "../../../../../shared/emails-meetings/MeetingDetailsDrawer";
import MeetingCreationModal from "../../../../../shared/emails-meetings/MeetingCreationModal";
import MemberLevelIntegrationProvider from "../../../../../shared/integration/MemberLevelIntegrationProvider";
import { getMeetingViewModel, meetingMatchesQuery, safeDate } from "../../../../../shared/emails-meetings/meetingUtils";

type FilterValue = "all" | "upcoming" | "past";

const UserMeetings = () => {
  const { userId }: any = useUserPageParams();
  const workspaceId = useWorkspaceStore((state) => state.workspace?.id);

  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState<FilterValue>("all");

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<AssociatedObject | null>(null);

  const [creationOpen, setCreationOpen] = useState(false);

  const pageSize = 50;

  const meetingsQuery = useInfiniteQuery<MeetingsResponse, Error>({
    queryKey: ["contact-meetings", workspaceId, userId, pageSize],
    queryFn: ({ pageParam }) =>
      getContactMeetings({
        workspace_id: workspaceId as string,
        contact_external_id: userId as string,
        limit: pageSize,
        offset: typeof pageParam === "number" ? pageParam : 0,
      }).then((res) => res.data),
    enabled: !!workspaceId && !!userId,
    staleTime: 1000 * 60 * 5,
    getNextPageParam: (lastPage) => {
      const nextOffset = (lastPage.offset ?? 0) + (lastPage.limit ?? pageSize);
      return nextOffset < lastPage.total_count ? nextOffset : undefined;
    },
  });

  const allMeetings = useMemo(() => {
    const pages = meetingsQuery.data?.pages ?? [];
    const flattened = pages.flatMap((page) => page.meetings ?? []);
    const seen = new Set<string>();
    return flattened.filter((meeting) => {
      if (seen.has(meeting.id)) return false;
      seen.add(meeting.id);
      return true;
    });
  }, [meetingsQuery.data]);

  const filteredMeetings = useMemo(() => {
    const now = new Date();

    const enriched = allMeetings
      .filter((meeting) => meetingMatchesQuery(meeting, searchQuery))
      .map((meeting) => {
        const view = getMeetingViewModel(meeting);
        const start = safeDate(view.startTime);
        return { meeting, start };
      })
      .filter(({ start }) => {
        if (filter === "all") return true;
        if (!start) return false;
        return filter === "upcoming" ? start > now : start <= now;
      });

    const upcoming = enriched
      .filter(({ start }) => !!start && start > now)
      .sort((a, b) => (a.start?.getTime() ?? 0) - (b.start?.getTime() ?? 0))
      .map(({ meeting }) => meeting);

    const past = enriched
      .filter(({ start }) => !start || start <= now)
      .sort((a, b) => (b.start?.getTime() ?? 0) - (a.start?.getTime() ?? 0))
      .map(({ meeting }) => meeting);

    if (filter === "upcoming") return upcoming;
    if (filter === "past") return past;
    return [...upcoming, ...past];
  }, [allMeetings, filter, searchQuery]);

  const totalCount = meetingsQuery.data?.pages?.[0]?.total_count ?? 0;

  const handleOpenMeeting = (meeting: AssociatedObject) => {
    setSelectedMeeting(meeting);
    setDetailsOpen(true);
  };

  const handleLoadMore = () => {
    meetingsQuery.fetchNextPage().catch(() => {});
  };

  const emptyMessage = useMemo(() => {
    if (searchQuery || filter !== "all") return "Try adjusting your search or filters.";
    return "No meetings associated with this contact yet.";
  }, [filter, searchQuery]);

  return (
    <MemberLevelIntegrationProvider>
      <div className="h-full bg-white flex flex-col overflow-hidden">
        <LoadingOverlay
          loaderProps={{ size: "sm" }}
          c="main.5"
          visible={meetingsQuery.isLoading}
          overlayProps={{ blur: 2 }}
        />

      <Stack gap={10} className="px-4 pt-4 pb-3">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Text fw={700} c="text.9" size="lg">Meetings</Text>
            <Text size="xs" c="text.5">
              {searchQuery ? `${filteredMeetings.length} of ${totalCount}` : `${totalCount}`} meetings
            </Text>
          </Stack>

          <Button
            size="sm"
            variant="filled"
            color="main"
            leftSection={<PlusIcon className="h-4 w-4" />}
            onClick={() => setCreationOpen(true)}
          >
            Create Meeting
          </Button>
        </Group>

        <Group gap="sm" align="center" wrap="wrap">
          <SegmentedControl
            value={filter}
            onChange={(value) => setFilter(value as FilterValue)}
            data={[
              { label: "All", value: "all" },
              { label: "Upcoming", value: "upcoming" },
              { label: "Past", value: "past" },
            ]}
            radius="md"
            size="sm"
            color="main"
          />

          <TextInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            placeholder="Search title, organizer, attendees..."
            leftSection={<MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />}
            size="sm"
            radius="md"
            style={{ flex: 1, minWidth: 220 }}
          />
        </Group>
      </Stack>

      <Divider />

      <ScrollArea className="flex-1" type="scroll" scrollHideDelay={500}>
        <Stack gap={0}>
          {meetingsQuery.error && (
            <div className="p-4">
              <Alert color="red" variant="light" radius="md">
                Failed to load meetings. Please try again.
              </Alert>
            </div>
          )}

          {!meetingsQuery.isLoading && !meetingsQuery.error && filteredMeetings.length === 0 && (
            <Center className="py-16">
              <Stack gap={6} align="center" className="px-4">
                <ThemeIcon variant="light" color="emerald" size={46} radius="xl">
                  <CalendarIcon className="h-5 w-5" />
                </ThemeIcon>
                <Text fw={700} c="text.9">No meetings found</Text>
                <Text size="sm" c="text.5" ta="center">
                  {emptyMessage}
                </Text>
              </Stack>
            </Center>
          )}

          <div className="divide-y divide-gray-100">
            {filteredMeetings.map((meeting) => (
              <MeetingListItem key={meeting.id} meeting={meeting} onOpen={handleOpenMeeting} />
            ))}
          </div>

          {meetingsQuery.hasNextPage && (
            <Center className="p-4">
              <Button
                variant="light"
                color="main"
                size="sm"
                loading={meetingsQuery.isFetchingNextPage}
                onClick={handleLoadMore}
              >
                Load more
              </Button>
            </Center>
          )}
        </Stack>
      </ScrollArea>

      <MeetingDetailsDrawer
        opened={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        meeting={selectedMeeting}
      />

        <MeetingCreationModal
          opened={creationOpen}
          onClose={() => setCreationOpen(false)}
        />
      </div>
    </MemberLevelIntegrationProvider>
  );
};

export default UserMeetings;
